import React from 'react';
import { FieldArray, useFormikContext } from 'formik';
import { Box, Button, Grid, Typography, IconButton, Paper, Zoom, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import InputField from 'components/formFields/InputField';
import TextAreaField from 'components/formFields/TextAreaField';
import AutocompleteField from 'components/formFields/autocompleteField';
import {
  appReleaseFormModel,
  featureFormModel,
} from '../formik/create/formModels/appReleaseFormModel';

const featureTypeOptions = [
  { label: '✨ Nueva funcionalidad', value: 'FEATURE' },
  { label: '🚀 Mejora', value: 'IMPROVEMENT' },
  { label: '🐞 Corrección de errores', value: 'FIX' },
  { label: '⚠️ Cambio importante', value: 'BREAKING_CHANGE' },
];
export const FeaturesFieldArray = () => {
  const { values, errors, touched } = useFormikContext<any>();

  return (
    <FieldArray
      name={appReleaseFormModel.formField.features.name}
      render={(arrayHelpers) => (
        <Box sx={{ mt: 2 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 4,
              pb: 2,
              borderBottom: '1px solid rgba(0,0,0,0.06)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #4f46e5 0%, #9333ea 100%)',
                  borderRadius: '12px',
                  p: 1.2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 8px 16px rgba(79, 70, 229, 0.25)',
                }}
              >
                <AutoAwesomeIcon sx={{ color: 'white', fontSize: 22 }} />
              </Box>
              <Box>
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 800,
                    background: 'linear-gradient(135deg, #0f172a 0%, #334155 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    letterSpacing: '-0.02em',
                  }}
                >
                  Novedades Incluidas
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Agrega las características o cambios de esta versión
                </Typography>
              </Box>
            </Box>
            <Button
              startIcon={<AddCircleOutlineIcon />}
              variant="contained"
              onClick={() =>
                arrayHelpers.push({
                  [featureFormModel.moduleName.name]: '',
                  [featureFormModel.title.name]: '',
                  [featureFormModel.description.name]: '',
                  [featureFormModel.type.name]: '',
                })
              }
              sx={{
                background: 'linear-gradient(135deg, #4f46e5 0%, #9333ea 100%)',
                borderRadius: '12px',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.95rem',
                px: 3,
                py: 1.2,
                boxShadow: '0 4px 14px 0 rgba(79, 70, 229, 0.4)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  boxShadow: '0 8px 25px rgba(79, 70, 229, 0.5)',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              Agregar Novedad
            </Button>
          </Box>

          {values.features && values.features.length > 0 ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
              {values.features.map((feature: any, index: number) => {
                const isError = errors.features && (errors.features as any[])[index];
                const isTouched = touched.features && (touched.features as any[])[index];

                return (
                  <Zoom in={true} style={{ transitionDelay: `${index * 50}ms` }} key={index}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2.5,
                        borderRadius: '16px',
                        border: '1px solid',
                        borderColor: 'rgba(226, 232, 240, 0.8)',
                        background: '#ffffff',
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: '0 4px 20px -10px rgba(0,0,0,0.05)',
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          boxShadow: '0 12px 30px -10px rgba(79, 70, 229, 0.15)',
                          transform: 'translateY(-2px)',
                          borderColor: 'rgba(79, 70, 229, 0.3)',
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '4px',
                          height: '100%',
                          background: 'linear-gradient(to bottom, #4f46e5, #9333ea)',
                          opacity: 0.85,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: '8px',
                              backgroundColor: 'rgba(79, 70, 229, 0.08)',
                              color: '#4f46e5',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 700,
                              fontSize: '0.85rem',
                              boxShadow: 'inset 0 0 0 1px rgba(79, 70, 229, 0.1)',
                            }}
                          >
                            {index + 1}
                          </Box>
                          <Typography
                            variant="subtitle2"
                            sx={{ fontWeight: 700, color: '#1e293b', fontSize: '0.95rem' }}
                          >
                            Detalle de la Novedad
                          </Typography>
                        </Box>
                        {values.features.length > 1 && (
                          <Tooltip title="Eliminar novedad" arrow placement="left">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => arrayHelpers.remove(index)}
                              sx={{
                                backgroundColor: 'rgba(239, 68, 68, 0.05)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  backgroundColor: 'rgba(239, 68, 68, 0.15)',
                                  transform: 'rotate(90deg) scale(1.1)',
                                  color: '#dc2626',
                                },
                              }}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>

                      <Grid container spacing={2}>
                        <Grid size={{ xs: 12, md: 3 }}>
                          <InputField
                            name={`${appReleaseFormModel.formField.features.name}[${index}].${featureFormModel.moduleName.name}`}
                            label={featureFormModel.moduleName.label}
                            error={isError && isError[featureFormModel.moduleName.name]}
                            fullWidth
                            variant="outlined"
                            autoComplete="off"
                            touched={isTouched && isTouched[featureFormModel.moduleName.name]}
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 3 }}>
                          <AutocompleteField
                            name={`${appReleaseFormModel.formField.features.name}[${index}].${featureFormModel.type.name}`}
                            label={featureFormModel.type.label}
                            options={featureTypeOptions}
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                          <InputField
                            name={`${appReleaseFormModel.formField.features.name}[${index}].${featureFormModel.title.name}`}
                            label={featureFormModel.title.label}
                            fullWidth
                            variant="outlined"
                            autoComplete="off"
                          />
                        </Grid>
                        <Grid size={{ xs: 12, md: 12 }}>
                          <TextAreaField
                            name={`${appReleaseFormModel.formField.features.name}[${index}].${featureFormModel.description.name}`}
                            label={featureFormModel.description.label}
                            fullWidth
                            variant="outlined"
                            autoComplete="off"
                            rows={5}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Zoom>
                );
              })}
            </Box>
          ) : (
            <Zoom in={true}>
              <Box
                sx={{
                  p: 5,
                  textAlign: 'center',
                  backgroundColor: 'rgba(248, 250, 252, 0.8)',
                  borderRadius: '20px',
                  border: '2px dashed rgba(203, 213, 225, 0.8)',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    borderRadius: '50%',
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    color: '#ef4444',
                  }}
                >
                  <DeleteOutlineIcon fontSize="large" />
                </Box>
                <Typography variant="h6" color="textPrimary" sx={{ fontWeight: 600 }}>
                  Sin Novedades
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 400 }}>
                  Debes incluir al menos una novedad para poder guardar el release. Haz clic en
                  "Agregar Novedad" para comenzar.
                </Typography>
              </Box>
            </Zoom>
          )}
        </Box>
      )}
    />
  );
};
